import { supabase } from '../config/supabase';
import { ItemSale } from '../types';

export interface MarkItemAsSoldInput {
  itemId: string;
  /** Platform user who bought the item (from a conversation about this item) */
  buyerId?: string;
  /** Free-text buyer name when the item was sold outside the platform */
  buyerName?: string;
  /** Sale price. Optional — the seller may leave it blank. */
  price?: number | null;
}

/**
 * The RPC raises bare codes so the UI can translate them; anything unexpected
 * falls through to the generic failure message.
 */
const SALE_ERROR_KEYS: Record<string, string> = {
  not_authenticated: 'notAuthenticated',
  item_not_found: 'loadFailed',
  not_the_seller: 'notAllowed',
  item_already_sold: 'alreadySold',
  invalid_price: 'invalidPrice',
  buyer_is_seller: 'buyerNotInConversation',
  buyer_not_in_conversation: 'buyerNotInConversation',
};

/** Maps a Postgres error message onto a `screens.markAsSold.*` key. */
export const saleErrorKey = (message: string | undefined): string => {
  if (!message) return 'failed';
  const match = Object.keys(SALE_ERROR_KEYS).find((code) => message.includes(code));
  return match ? SALE_ERROR_KEYS[match] : 'failed';
};

export const salesService = {
  /**
   * Register a sale for an item and mark the item as sold.
   *
   * When buyerId is provided the sale is registered as "within platform";
   * otherwise it counts as a sale outside the platform (with an optional
   * free-text buyer name). The item status is set to 'sold', which removes
   * it from Browse (Browse only shows 'available' items).
   *
   * @param input - itemId plus either buyerId (platform sale) or buyerName (external sale)
   * @returns Promise<ItemSale> The registered sale
   * @throws Error if not authenticated or if the insert/update fails
   */
  async markItemAsSold({ itemId, buyerId, buyerName, price }: MarkItemAsSoldInput): Promise<ItemSale> {
    // One RPC rather than insert-then-update: the sale row and the item status
    // must move together, or a failed second call leaves the item sold-but-
    // visible and a retry registers the sale twice. The RPC also validates that
    // the buyer actually had a conversation about this item, which RLS cannot
    // express on its own.
    const { data: sale, error } = await supabase.rpc('record_item_sale', {
      p_item_id: itemId,
      p_buyer_id: buyerId ?? null,
      p_buyer_name: buyerName?.trim() || null,
      p_price: price ?? null,
    });

    if (error) throw error;
    if (!sale) throw new Error('Failed to register the sale');

    return sale as ItemSale;
  },

  /**
   * Number of sales the current user has registered (shown on the profile
   * stats row). Returns 0 when signed out or when the query fails.
   */
  async getMySalesCount(): Promise<number> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const { count, error } = await supabase
      .from('item_sales')
      .select('id', { count: 'exact', head: true })
      .eq('seller_id', user.id);

    if (error) {
      console.warn('Failed to load sales count:', error.message);
      return 0;
    }
    return count ?? 0;
  },
};
